/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { ClassAttributes, HTMLAttributes, useCallback, useState } from 'react';
import { WabaIntegration } from '../../../typings/waba';
import { QSTypography } from '../../atoms';
import { ButtonBase, Icon, SxProps, TypographyProps } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { BORDER_COLOR } from '../../../typings';
import DownChevron from '@mui/icons-material/ChevronLeft';
import { Virtuoso } from 'react-virtuoso';
import QSTextInput from '../text-input';
import { Search } from '@mui/icons-material';
import debounce from '../../../utils/debounce';
import Loader from '../loader';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import DTDropdown from '../custom-dropdown';
import { TextEllipsisStyles } from '../../emotion';
import WabaIntegrationIcon from '../waba-integration-logo';

interface WabaDropdownProps {
  setSelectedWaba: (waba: WabaIntegration) => any;
  wabaIntegrationsFetchMore: () => Promise<void>;
  wabaIntegrationsData: WabaIntegration[] | undefined;
  fetchingMoreWabaIntegrations: boolean;
  searchTerm: string | undefined;
  setSearchTerm: React.Dispatch<React.SetStateAction<string | undefined>>;
  selectedWaba: WabaIntegration | undefined;
  headerTitle: string;
  headerTitleStyles?: TypographyProps;
  containerProps?: ClassAttributes<HTMLDivElement> & HTMLAttributes<HTMLDivElement>;
  inputStylesSx?: SxProps;
  showWabaNumber?: boolean;
  hoverBorderColor?: string;
}

const WabaSelectorDropdown = ({
  setSelectedWaba,
  wabaIntegrationsFetchMore,
  wabaIntegrationsData,
  fetchingMoreWabaIntegrations,
  searchTerm,
  setSearchTerm,
  selectedWaba,
  headerTitle,
  headerTitleStyles,
  containerProps,
  inputStylesSx,
  showWabaNumber = false,
  hoverBorderColor,
}: WabaDropdownProps) => {
  const [dropdown, setDropdown] = useState(false);

  const handleWabaSelect = useCallback(
    (waba: WabaIntegration) => {
      setSelectedWaba(waba);
      setDropdown(false);
      setSearchTerm('');
    },
    [setSelectedWaba, setDropdown, setSearchTerm]
  );

  const debouncedSetSearch = debounce(setSearchTerm, 300);

  return (
    <DTDropdown
      trigger={
        <div
          css={css`
            padding: 8px;
            border-radius: 4px;
            cursor: pointer;
            display: flex;
            align-items: center;
            width: 100%;
          `}
        >
          <QSTypography
            sx={{
              flex: 1,
              fontWeight: 600,
              ...inputStylesSx,
            }}
          >
            {selectedWaba?.displayName ?? selectedWaba?.name ?? selectedWaba?.phoneNumber ?? 'Select WABA'}
            {showWabaNumber ? (
              <span
                css={css`
                  font-weight: 500;
                  margin-left: 8px;
                  font-size: 14px;
                  color: #7a7a7a;
                `}
              >
                ({selectedWaba?.phoneNumber})
              </span>
            ) : null}
          </QSTypography>
          <DownChevron
            css={css`
              height: 1em;
              width: 1em;
              font-size: 1.5em;
              fill: currentColor;
              color: rgba(0, 0, 0, 0.54);
              ${dropdown ? 'transform: rotate(90deg)' : 'transform: rotate(-90deg)'};
            `}
          />
        </div>
      }
      open={dropdown}
      setOpen={setDropdown}
      position="bottom"
      customWidth="300px"
      headerTitle={headerTitle}
      headerTitleStyles={headerTitleStyles}
      containerProps={containerProps}
      hoverBorderColor={hoverBorderColor}
    >
      <div
        css={css`
          background: #fff;
          z-index: 10;
          display: flex;
          flex-direction: column;
          box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.13);
          border-radius: 4px;
          height: 300px;
        `}
      >
        <QSTextInput
          sx={{ width: '100%', padding: '8px' }}
          onChange={(e) => debouncedSetSearch(e.target.value)}
          defaultValue={searchTerm}
          placeholder="Search"
          textInputProps={{
            sx: {
              background: 'white',
              border: `1px solid ${BORDER_COLOR}`,
              borderRadius: '6px',
              minHeight: '35px',
              justifyContent: 'center',
              padding: '0 8px',
              '.MuiInputBase-root::before': {
                borderBottom: 'none !important',
              },
              '.MuiInputBase-root,.MuiInput-root': {
                '&::before': {
                  border: 'none',
                },
                '&::after': {
                  border: 'none',
                },
                '&:hover:not(.Mui-disabled)::before': {
                  border: 'none',
                },
                'input::placeholder, textArea::placeholder': {
                  color: '#7B7B7B',
                },
              },
            },
            InputProps: {
              startAdornment: (
                <Icon
                  css={css`
                    display: flex;
                    align-items: center;
                  `}
                >
                  <Search
                    css={css`
                      width: 20px;
                      height: 20px;
                    `}
                  />
                </Icon>
              ),
            },
            className: 'nofocus',
            autoFocus: true,
            autoComplete: 'off',
            onKeyDown: (e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
              }
            },
          }}
        />
        <Virtuoso
          totalCount={(wabaIntegrationsData ?? []).length}
          data={wabaIntegrationsData ?? []}
          endReached={wabaIntegrationsFetchMore}
          itemContent={(_, waba) => (
            <ButtonBase<'div'>
              css={css`
                display: flex;
                align-items: center;
                justify-content: flex-start;
                gap: 8px;
                width: 100%;
                padding: 8px;
                cursor: pointer;
                border-top: 1px solid ${BORDER_COLOR};
                background-color: ${selectedWaba?.id === waba.id ? 'rgba(93, 183, 121, 0.10)' : '#ffffff'};

                &:hover {
                  background-color: ${selectedWaba?.id === waba.id ? 'rgba(93, 183, 121, 0.10)' : '#f5f5f5'};
                }
              `}
              onClick={() => handleWabaSelect(waba)}
              component="div"
            >
              <WabaIntegrationIcon
                wabaIntegrationLogo={waba.logo ?? ''}
                wabaIntegrationName={waba.displayName ?? waba.name ?? waba.phoneNumber ?? ''}
                channelType={waba.type}
              />
              <div
                css={css`
                  width: calc(100% - 40px - 24px - 2 * 8px);
                  display: flex;
                  flex-direction: column;
                  align-items: flex-start;
                  gap: 4px;
                  overflow: hidden;
                `}
              >
                <QSTypography
                  css={css(
                    css`
                      font-size: 14px;
                      font-weight: 400;
                    `,
                    TextEllipsisStyles
                  )}
                >
                  {waba.displayName ?? waba.name ?? waba.phoneNumber ?? ''}
                </QSTypography>
                {waba.phoneNumber ? (
                  <QSTypography
                    css={css`
                      font-size: 12px;
                      color: #5a5a5a;
                    `}
                  >
                    {waba.phoneNumber}
                  </QSTypography>
                ) : null}
              </div>
              {selectedWaba?.id === waba.id && (
                <CheckIcon
                  css={css`
                    height: 24px;
                    min-height: 24px;
                    width: 24px;
                    min-width: 24px;
                    color: #3f906c;
                  `}
                />
              )}
            </ButtonBase>
          )}
          components={{
            EmptyPlaceholder: () => {
              return (
                <div
                  css={css`
                    height: 100%;
                    width: 100%;
                    display: flex;
                    flex-direction: column;
                    column-gap: 8px;
                    justify-content: center;
                    align-items: center;
                    padding: 16px;
                    color: #8484a8;
                  `}
                >
                  <Icon>
                    <ReportProblemIcon />
                  </Icon>
                  <QSTypography
                    css={css`
                      margin: 0px;
                      font-family: Roboto, sans-serif;
                      font-weight: 400;
                      font-size: 1rem;
                      line-height: 1.5;
                      text-align: center;
                    `}
                  >
                    No WABA's found
                  </QSTypography>
                </div>
              );
            },
            Footer: () => {
              if (!fetchingMoreWabaIntegrations) {
                return null;
              }
              return (
                <div
                  css={css`
                    height: 100%;
                    width: 100%;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    padding: 16px;
                  `}
                >
                  <Loader size={24} />
                </div>
              );
            },
          }}
        />
      </div>
    </DTDropdown>
  );
};

export default WabaSelectorDropdown;
